import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda, selectPeriodo, selectUser, setupBusy} from "@commons/core.slice";
import PeriodoRif from "@components/PeriodoRif";
import Banner, {bannerStyle} from "@components/Banner";
import Loading from "@components/dataTable/Loading";
import {PeriodoForm, useHandlersPeriodoForm} from "@hooks/useHandlersPeriodoForm";
import {IndiceMese, Periodo} from "@commons/types";
import {FormProvider, useForm} from "react-hook-form";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {SelectLavoratore} from "../comps/SelectLavoratore";
import {defaultOpt, PresenzeContext, usePresenze, UsePresenzeOptions} from "@commons/hooks/usePresenze";
import {CalendarioWF} from "./calendario.WF";
import {useTabellaWF} from "@commons/services/presenze_api/workflow.queries";
import {DateTime} from "luxon";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import Tabs4Employee, {useSelectorPolicy} from "./workflowUI";

interface WorkflowEmployeeForm extends PeriodoForm {
    idLavoratore: number;
}

export const Workflow: React.FC = () => {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const aziendaRecord = useAppSelector(selectAzienda);
    const azienda: AziendaRecord | undefined = aziendaRecord ? aziendaRecord : undefined;// Azienda corrente
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [disabledApply, setDisabledApply] = useState<boolean>(true);
    const [showLavoratori, setNextShowLavoratori] = useState<boolean>(true);
    const [showPeriodoWF, setNextShowPeriodoWF] = useState<boolean>(true);

    useEffect(() => {
        console.log('start-up...');
        dispatch(setupBusy(true));
    }, [dispatch]);

    // Gestione selezione anno/mese
    const [periodo, setNextPeriodo] = useState<Periodo>(useAppSelector(selectPeriodo));

    const {data: tabellaWF,} = useTabellaWF(aziendaRecord ? {idDominio: aziendaRecord.idDominio!, idTabella: aziendaRecord.idTabellaWF} : null); // Carico tabella impianto WF
    const minDate = useMemo<string>(() => tabellaWF && tabellaWF.decorrenza ? tabellaWF.decorrenza : DateTime.now().minus({years: 1}).toFormat('yyyy-LL-dd'), [tabellaWF]);
    const maxDate = useMemo<string>(() => tabellaWF && tabellaWF.fineValidita ? tabellaWF.fineValidita : DateTime.now().plus({years: 1}).toFormat('yyyy-LL-dd'), [tabellaWF]);

    const defaultValues: WorkflowEmployeeForm = {anno: periodo.anno, mese: periodo.mese, idLavoratore: 0};
    const form = useForm<WorkflowEmployeeForm>({defaultValues});
    const {getValues, setValue, handleSubmit, control, register,} = form;

    const chiudiElenco = () => {
    }

    // Chiudo elenco giornaliero al variare dell'azienda
    useEffect(() => {
        chiudiElenco();
    }, [azienda]);

    // Hook di caricamento e gestione Presenze
    const opt = useMemo((): UsePresenzeOptions => {
        return {
            ...defaultOpt,
            employeeOrder: true,
            //selFirst: false,
            user,
            isPaginaWF: true,
            onLoadComplete: (_lavoratori: LavoratorePresenzeRecord[], _idLavoratore: number | null) => {
                console.log('Caricamento lavoratori completato!');
                dispatch(setupBusy(false));
            },
        }
    }, [dispatch, user]);

    //useWhatChanged([azienda, periodo, opt, minDate, maxDate], 'azienda, periodo, opt, minDate, maxDate');
    const {
        reloadAll, lavoratore, selectLavoratore, lavoratori,
        isLoading: isLoadingPresenze, updLavoratore, getLavoratorePrev, getLavoratoreNext, calendarioChiusure, listaGiustificativi, listaTerminali,
        context,
    } = usePresenze(azienda, periodo, opt, minDate, maxDate);
    const {contratti, selectedPolicyTab, setSelectedPolicyTab} = useSelectorPolicy(lavoratori);

    // al cambiamento di anno o mese
    const onChange = useCallback((_anno: number, _mese: number) => {
        setNextShowLavoratori(false);
        setNextShowPeriodoWF(false);
        setDisabledApply(false);
    }, []);

    // alla conferma della selezione di anno o mese
    const onApply = useCallback((anno: number, mese: IndiceMese) => {
        setNextShowLavoratori(true);
        setNextPeriodo({anno, mese});
        setDisabledApply(true);
    }, []);

    const onReloadPeriodo = () => {
        reloadAll();
    };

    const periodoRifOptions = useHandlersPeriodoForm(form, onChange, onApply, onReloadPeriodo, {autocommit: true});

    // A cambiamento del lavoratore
    const onChangeLavoratore = useCallback((_lavoratore?: LavoratorePresenzeRecord) => {
        setNextShowPeriodoWF(false);
    }, []);

    // Alla conferma del lavoratore
    const onApplyLavoratore = useCallback((form: WorkflowEmployeeForm) => {
        setIsLoading(true);
        selectLavoratore(form.idLavoratore, () => {
            setIsLoading(false);
            setNextShowPeriodoWF(true);
        });
    }, [selectLavoratore]);

    // Al lavoratore precedente o successivo (conferma automatica del lavoratore!)
    const changeLavoratore = (next: LavoratorePresenzeRecord | null) => {
        if (lavoratore && next) {
            if (next.id !== lavoratore.id) {
                setValue("idLavoratore", next.id);
                handleSubmit(onApplyLavoratore)();
            }
        }
    }

    // allineo combo lavoratori con lavoratore da usePresenze
    useEffect(() => {
        if (getValues('idLavoratore') !== lavoratore?.id)
            setValue('idLavoratore', lavoratore?.id || 0);
    }, [getValues, setValue, lavoratore]);

    // Selezione automatica del contratto se nessun lavoratore selezionato e un solo contratto selezionabile
    useEffect(() => {
        if (getValues('idLavoratore') === 0
            && contratti.length === 1
            //&& selectedPolicyTab === PolicyTabEnum.MySelf
        ) {
            setValue('idLavoratore', contratti[0].id);
            handleSubmit(onApplyLavoratore)();
        }
    }, [getValues, setValue, contratti, onApplyLavoratore, handleSubmit]);

    //const checkSviluppo = lavoratore && lavoratore.weeks !== null && lavoratore.weeks.length !== 0 && gestione.weeks!.length !== 0;
    const checkSviluppo = lavoratore && lavoratore.weeks !== null && lavoratore.weeks.length !== 0; // todo verificare!!!

    const periodoValidoXWF = (): boolean => {
        let consolida = true;
        if (tabellaWF && (tabellaWF.decorrenza || tabellaWF.fineValidita)) {
            const startDate = DateTime.local(periodo.anno, periodo.mese); // return es. 2023-09-01T00:00:00
            const endDate = startDate.endOf('month').startOf('day') // endOf => return es. 2023-09-30T23:59:59.999-05:00 --- con startOf imposto ora a '00:00.000-05:00'

            // Se è impostata la data di decorrenza della tabellaWF
            if (tabellaWF.decorrenza) {
                const decorrenzaTabellaWF = DateTime.fromISO(tabellaWF.decorrenza);
                consolida = startDate.toMillis() >= decorrenzaTabellaWF.toMillis();

                // Se è impostata anche la data di fine validità della tabellaWF
                if (consolida && tabellaWF.fineValidita) {
                    const fineValiditaTabellaWF = DateTime.fromISO(tabellaWF.fineValidita);
                    consolida = fineValiditaTabellaWF.toMillis() >= endDate.toMillis();
                }
            }
        }
        return consolida;
    };

    const onUpdateRichiesteWF = (richiesteWF: PresenzeCausaleWFRecord[]) => {
        updLavoratore(lavoratore!, (lavoratore) => {
            lavoratore.causaliWF = richiesteWF;
            return {lavoratore};
        });
    }
    //console.log('isLoading', isLoading)
    //console.log('isLoadingPresenze', isLoadingPresenze)

    const periodoValidoWF = periodoValidoXWF();


    return <>
        <Loading loading={isLoading || isLoadingPresenze}>
            <div className="mb-4">
                <FormProvider {...form} >
                    <form onSubmit={handleSubmit(onApplyLavoratore)}>
                        <PeriodoRif nameAnno="anno" nameComboMese="mese" className="w-2/3" {...periodoRifOptions}
                                    disabled={disabledApply}/>
                        {showLavoratori && periodoValidoWF && <div className="pt-2">
                            <Tabs4Employee selectedPolicyTab={selectedPolicyTab} onchangePolicy={(oldPolicy, newPolicy) => {
                                setSelectedPolicyTab(newPolicy);
                                onChangeLavoratore();
                                setValue('idLavoratore', 0);
                            }
                            }/>
                            <SelectLavoratore<WorkflowEmployeeForm> lavoratori={contratti} name="idLavoratore"
                                                                    className="w-2/3"
                                                                    hasApply={true}
                                                                    onChange={onChangeLavoratore}
                                                                    next={() => changeLavoratore(getLavoratoreNext())}
                                                                    prev={() => changeLavoratore(getLavoratorePrev())}/>
                        </div>}
                    </form>
                </FormProvider>
            </div>

            {/* BANNERS DI SELEZIONE PERIODO E LAVORATORE */}
            {!showLavoratori && periodoValidoWF && <Banner style={bannerStyle.warning} text={<>Seleziona un periodo e premi il bottone <strong>Applica</strong>!</>}/>}
            {!showPeriodoWF && periodoValidoWF && showLavoratori &&
                <Banner style={bannerStyle.warning} text={<>Seleziona un lavoratore e premi il bottone <strong>Applica</strong>!</>}/>}

            {/* CALENDARIO WORKFLOW */}
            {(checkSviluppo && periodoValidoWF && showPeriodoWF) && tabellaWF &&
                <PresenzeContext.Provider value={context}>
                    <CalendarioWF periodo={periodo} calendarioChiusure={calendarioChiusure} lavoratore={lavoratore} tabellaWF={tabellaWF} onUpdateRichiesteWF={onUpdateRichiesteWF}
                                  listaGiustificativi={listaGiustificativi} listaTerminali={listaTerminali}
                    />
                </PresenzeContext.Provider>
            }

            {/* MESE NON SVILUPPATO */}
            {(lavoratore && !checkSviluppo && showPeriodoWF) &&
                <Banner style={bannerStyle.danger}
                        text={
                            <>Per il lavoratore <span className="font-bold">{lavoratore.descrizione}</span> [#{lavoratore!.id}], non è presente lo <u>sviluppo del mese</u>.</>
                        }/>}

            {/* SE IL WF NON è ATTIVO NEL PERIODO (La data di decorrenza deve corrispondere al primo giorno del mese, mentre la data di fine deve corrispondere all'ultimo giorno del mese!) */}
            {!periodoValidoWF &&
                <Banner style={bannerStyle.secondary} text={<>Il modulo workflow <span className="font-bold">non è abilitato</span> in questo periodo.</>}/>}

            {/* PROBLEMA!! */}
            {!(isLoading || isLoadingPresenze) && showPeriodoWF && !lavoratore &&
                <Banner style={bannerStyle.danger} text={`C'è un problema! / Non ci sono lavoratori per l'UL selezionata!`}/>}
        </Loading>
    </>;
}